import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';

import { Text } from '../../../shared/uiComponents';
import { InfoRow } from '../../../components/StyledComponents';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getSessionBehaviors } from '../../../redux/State/clientSlice/dataCollectionSlice';
import { getSessionTargets } from '../../../redux/State/clientSlice/sessionSlice';

const Targets = () => {
	const [params] = useSearchParams();
	const dispatch = useDispatch<DispatchProperties>();

	const sessionTargets = useSelector((state) => state.session.sessionTargets);

	useEffect(() => {
		const sessionId = params.get('sessionId');
		if (!sessionId) return;
		dispatch(getSessionTargets(sessionId));
	}, [params, dispatch]);

	if (!sessionTargets.length) return null;

	return (
		<>
			<Text title="Session targets:" fontWeight="semiBold" className="marginBottom16" font="header" type="h6" />

			{sessionTargets.map((target, index) => (
				<div
					key={index}
					className="marginBottom16"
					style={{
						border: '1px solid black',
						borderRadius: '12px',
						padding: '8px',
						width: '100%',
					}}
				>
					<InfoRow className="marginBottom8">
						<Text title="Skill area name:" fontWeight="semiBold" />
						<Text title={target.skillArea.name} fontWeight="semiBold" />
					</InfoRow>
					<InfoRow className="marginBottom8">
						<Text title="Program name:" fontWeight="semiBold" />
						<Text title={target.program.name} fontWeight="semiBold" />
					</InfoRow>
					<InfoRow className="marginBottom8">
						<Text title="Target name:" fontWeight="semiBold" />
						<Text title={target.target.name} fontWeight="semiBold" />
					</InfoRow>
					<InfoRow className="marginBottom8">
						<Text title="Independent:" fontWeight="semiBold" />
						<Text title={`${target.correctPercentage}%`} fontWeight="semiBold" />
					</InfoRow>
					<InfoRow className="marginBottom8">
						<Text title="Prompted:" fontWeight="semiBold" />
						<Text title={`${100 - target.correctPercentage}%`} fontWeight="semiBold" />
					</InfoRow>
					<InfoRow className="marginBottom8">
						<Text title="Trials:" fontWeight="semiBold" />
						<Text title={target.trials} fontWeight="semiBold" />
					</InfoRow>
				</div>
			))}
		</>
	);
};

const Behaviors = () => {
	const [params] = useSearchParams();
	const dispatch = useDispatch<DispatchProperties>();

	const sessionBehaviors = useSelector((state) => state.dataCollection.sessionBehaviors);

	useEffect(() => {
		const sessionId = params.get('sessionId');
		if (!sessionId) return;
		dispatch(getSessionBehaviors(sessionId));
	}, [params, dispatch]);

	if (!sessionBehaviors.length) return null;

	return (
		<>
			<Text title="Behaviors:" type="h6" font="header" fontWeight="semiBold" className="marginBottom16" />

			{sessionBehaviors.map((behavior, index) => (
				<div
					key={index}
					className="marginBottom16"
					style={{
						border: '1px solid black',
						borderRadius: '12px',
						padding: '8px',
						width: '100%',
					}}
				>
					<InfoRow className="marginBottom8">
						<Text title="Behavior name:" fontWeight="semiBold" />
						<Text title={behavior.name} fontWeight="semiBold" />
					</InfoRow>
					<InfoRow className="marginBottom8">
						<Text title="Category name:" fontWeight="semiBold" />
						<Text title={behavior.category.name} fontWeight="semiBold" />
					</InfoRow>
					<InfoRow className="marginBottom8">
						<Text title="Recording type:" fontWeight="semiBold" />
						<Text title={behavior.recordingType.text} fontWeight="semiBold" />
					</InfoRow>
					{behavior.count !== null && (
						<InfoRow className="marginBottom8">
							<Text title="Count:" fontWeight="semiBold" />
							<Text title={`${behavior.count}`} fontWeight="semiBold" />
						</InfoRow>
					)}
					{behavior.durationInstances.length > 0 && (
						<InfoRow className="marginBottom8">
							<Text title="Instances" fontWeight="semiBold" />
							<div>
								{behavior.durationInstances.map((durationInstance) => (
									<Text
										key={durationInstance.id}
										title={`${dayjs(durationInstance.createdAt).format('MM/DD/YYYY hh:mm A')} - ${durationInstance.duration}`}
										fontWeight="semiBold"
									/>
								))}
							</div>
						</InfoRow>
					)}

					{behavior.duration !== null && (
						<InfoRow className="marginBottom8">
							<Text title="Total duration:" fontWeight="semiBold" />
							<Text title={`${behavior.duration} seconds`} fontWeight="semiBold" />
						</InfoRow>
					)}
				</div>
			))}
		</>
	);
};

export { Targets, Behaviors };
